import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket } from "lucide-react";

export const Route = createFileRoute("/termsofpurchasecustomdesign")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Terms of Purchase — Custom Design — Three Day Launch" },
      { name: "description", content: "Terms of Purchase for the Three Day Launch custom website design offer." },
      { property: "og:title", content: "Terms of Purchase — Custom Design — Three Day Launch" },
      { property: "og:description", content: "Terms of Purchase for the Three Day Launch custom website design offer." },
      { property: "og:url", content: "https://threedaylaunch.com/termsofpurchasecustomdesign" },
    ],
    links: [{ rel: "canonical", href: "https://threedaylaunch.com/termsofpurchasecustomdesign" }],
  }),
  component: TermsOfPurchasePage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-2xl font-semibold mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function TermsOfPurchasePage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
          Terms of Purchase — Custom Design
        </h1>

        <div className="mt-10 space-y-10 text-foreground/90 leading-relaxed">
          <p>
            By purchasing the Three Day Launch Website Design offer (“Offer”), you (“Client”) agree to the following Terms of Purchase with Three Day Launch / Sierra Lin Design (“Freelancer”).
          </p>

          <Section title="1. Offer Overview">
            <p>
              This Offer includes the design and build of a custom website with up to four (4) main website pages. Standard page examples may include Home, About, Services, Contact, or similar core business pages.
            </p>
            <p>
              This Offer may also include must-have back-end website pages, such as a Blog page, 404 page, Privacy Policy page, and Terms and Conditions page. Client understands that Freelancer does not provide legal advice or legal policy drafting. Client is responsible for providing, reviewing, and approving all legal policy content, including Privacy Policy, Terms and Conditions, disclaimers, accessibility statements, cookie notices, and any other legal website content.
            </p>
            <p>
              The website design includes mobile-responsive design, basic SEO-friendly setup, and connection to Client’s preferred email marketing platform where technically available. “SEO-friendly setup” means basic on-page and technical best-practice setup; it does not guarantee search rankings, traffic, AI search visibility, leads, sales, or conversions.
            </p>
            <p>
              Final page structure, platform, design direction, and implementation approach are determined by Freelancer based on the selected package, Client’s business needs, project timeline, and materials provided by Client.
            </p>
            <p>
              Any pages, functionality, copywriting, integrations, automations, funnels, e-commerce features, course setup, membership setup, custom code, advanced SEO, advanced accessibility work, database functionality, ongoing maintenance, hosting management, or other work not specifically listed in this Offer is excluded and may require an additional fee.
            </p>
          </Section>

          <Section title="2. Payment Terms">
            <p>
              Client agrees to pay the project price shown at checkout, on the invoice, or otherwise agreed to in writing.
            </p>
            <p>
              If Client selects or is offered a payment plan, the payment plan is a method of paying the total project fee and does not create an ongoing subscription or ongoing service relationship. Client remains responsible for all scheduled installment payments for the purchased project.
            </p>
            <p>
              Client authorizes Freelancer and/or its payment processor to charge the payment method provided according to the payment schedule agreed upon at checkout or in writing.
            </p>
            <p>
              Failure to make a required payment may result in delayed work, paused work, rescheduling, delayed launch, or withholding of final delivery until the account is brought current.
            </p>
            <p>
              Unless otherwise agreed in writing, all amounts must be paid in full before final project completion or launch.
            </p>
          </Section>

          <Section title="3. No Refund Policy">
            <p>
              All sales are final. Due to reserved project time, digital service commencement, strategy, preparation, and custom design work involved, refunds are not provided except where required by law.
            </p>
            <p>
              Cancellation, project delay, lack of response, missed deadlines, failure to provide materials, failed payment, or Client’s decision not to move forward does not cancel payment obligations already incurred under this Offer.
            </p>
            <p>
              If Client purchased using a payment plan, cancellation or abandonment of the project does not automatically cancel remaining scheduled payments for work or project capacity already purchased.
            </p>
          </Section>

          <Section title="4. Domain Registration and Third-Party Costs">
            <p>
              Domain registration, domain renewals, transfers, DNS fees, email hosting, Lovable subscriptions or usage fees, paid plugins, premium fonts, stock assets, third-party apps, software subscriptions, and related third-party costs are not included unless expressly stated in writing.
            </p>
            <p>
              Client is solely responsible for maintaining ownership, payment, access, and good standing for their domain, Lovable account, hosting or platform plan, email platform, payment processor, email marketing platform, and any third-party tools connected to the website.
            </p>
            <p>
              Client should maintain ownership of their domain through their own domain registrar account unless otherwise agreed in writing.
            </p>
            <p>
              Freelancer is not responsible for domain ownership disputes, domain expiration, registrar issues, DNS delays, loss of account access, insufficient platform credits or account limits, or failure by Client to maintain required subscriptions.
            </p>
            <p>
              Freelancer is not responsible for downtime, outages, bugs, account restrictions, platform limitations, billing issues, plugin conflicts, deliverability issues, registrar issues, policy changes, security incidents, data loss, or service interruptions caused by Lovable or other third-party platforms or providers.
            </p>
          </Section>

          <Section title="5. Client-Owned Lovable Account and Ongoing Website Management">
            <p>
              The website will be created, developed, and/or published through Client’s own Lovable account or workspace unless otherwise agreed in writing.
            </p>
            <p>
              Client is responsible for creating and maintaining their Lovable account and for providing Freelancer with the access reasonably necessary to perform the website design services.
            </p>
            <p>
              Client is responsible for all Lovable subscription costs, hosting costs, usage fees, credits, platform upgrades, or other charges associated with Client’s account.
            </p>
            <p>
              Because the website is created in Client’s own account, Client retains control of their account and is responsible for maintaining access to their login credentials, billing information, and account settings.
            </p>
            <p>
              Once the project has been completed and delivered, Client is responsible for managing, maintaining, updating, hosting, backing up, securing, and operating the website unless Client separately purchases ongoing services from Freelancer.
            </p>
            <p>
              Ongoing website management, maintenance, troubleshooting, updates, edits, hosting administration, technical support, SEO services, blog writing, platform support, or future website work are not included in this one-time Website Design Offer unless expressly stated in writing.
            </p>
            <p>
              Freelancer is not responsible for changes, errors, downtime, broken functionality, security issues, lost content, platform changes, third-party updates, or other issues that occur after project delivery as a result of Client changes, third-party changes, platform updates, account issues, or circumstances outside Freelancer’s control.
            </p>
            <p>
              Any assistance requested after completion of the included project scope may be subject to Freelancer’s availability and may require a separate agreement or additional fee.
            </p>
            <p>
              Client is responsible for maintaining their own copies of important business materials, including website copy, brand assets, images, legal policies, blog content, lead magnets, customer information, and third-party platform data. Freelancer does not guarantee permanent backup, storage, or recovery of Client materials.
            </p>
          </Section>

          <Section title="6. Client Responsibilities and Project Readiness">
            <p>
              Client agrees to provide all requested questionnaires, content, images, brand materials, logins, account invitations, domain access, email platform access, approvals, feedback, and communication by the deadlines provided.
            </p>
            <p>
              Client’s project questionnaire and required prep materials must be completed at least seven (7) days before the scheduled kickoff date. If Client does not complete the required prep materials by the deadline, Freelancer may reschedule the project to the next available opening.
            </p>
            <p>
              The 3-day project timeline begins only after Client has provided all required materials, account access, approvals, and payments.
            </p>
            <p>
              If Client delays, misses deadlines, provides incomplete materials, requests major changes, or is unavailable for required feedback, the timeline may be extended, paused, rescheduled, or converted into a standard project timeline at Freelancer’s discretion.
            </p>
            <p>
              Client must be available by email or the agreed communication method during the build window to answer questions, provide feedback, and approve work.
            </p>
            <p>
              Freelancer is not responsible for missed launch dates caused by Client delay, missing information, lack of access, late approvals, third-party issues, domain or DNS issues, Lovable account issues, payment issues, or changes in scope.
            </p>
          </Section>

          <Section title="7. Launch and Delivery">
            <p>
              Freelancer will make reasonable efforts to complete and launch the website on Day 3 of the scheduled build window when Client has provided all required materials, access, approvals, and payment.
            </p>
            <p>
              If the website cannot be launched due to Client delay, missing approvals, missing domain access, incomplete content, legal policy delays, third-party issues, platform issues, or other circumstances outside Freelancer’s control, the project may be considered delivered as a completed website draft or staged website within Client’s account.
            </p>
            <p>
              Any additional launch assistance required because of Client delays, missing materials, third-party problems, or circumstances outside the original project scope may be quoted separately.
            </p>
            <p>
              Delivery may mean completion of the website within Client’s Lovable account, publication of the website, connection to Client’s custom domain, or another agreed project completion milestone depending on Client readiness and the scope of the Offer.
            </p>
            <p>
              Because the website is built in Client’s own Lovable account, Client will retain access to the website through their own account after delivery.
            </p>
            <p>
              Freelancer may withhold final launch or completion of remaining project work until Client has completed all required payments and the Client’s account with Freelancer is current.
            </p>
            <p>
              Once the included project scope and revision period are complete, Freelancer’s obligations under this one-time Website Design Offer are considered fulfilled.
            </p>
          </Section>

          <Section title="8. Post-Delivery Support and Maintenance">
            <p>
              This Offer is for a one-time website design project and does not include ongoing Website Concierge services, ongoing maintenance, hosting management, or continuing technical support unless expressly stated in writing.
            </p>
            <p>
              After project completion and delivery, Client assumes responsibility for the ongoing management and operation of the website.
            </p>
            <p>
              Any future edits, updates, troubleshooting, redesigns, new pages, new functionality, integrations, platform assistance, or other services requested after the project is complete are outside the scope of this Offer and may require a separate purchase or agreement.
            </p>
            <p>
              Freelancer does not guarantee future availability for ongoing website work unless Client has separately purchased an ongoing service.
            </p>
          </Section>

          <Section title="9. Revision Limits">
            <p>
              This Offer includes up to three (3) rounds of revisions during the active project window, unless otherwise stated in writing.
            </p>
            <p>
              A revision round means one consolidated set of Client feedback submitted at one time. Client agrees to gather all feedback, edits, and requested changes into a single organized response for each revision round.
            </p>
            <p>
              Revision rounds may include reasonable adjustments to existing design, layout, copy placement, images, or page content.
            </p>
            <p>
              Revision rounds do not include new pages, new functionality, changes to previously approved direction, full redesigns, platform changes, or requests outside the original scope.
            </p>
            <p>
              Unused revision rounds expire at launch or within one (1) day of delivery, whichever comes first.
            </p>
            <p>
              Additional revisions or changes requested after the included revision period may be billed separately and are subject to Freelancer’s availability.
            </p>
          </Section>

          <Section title="10. Website Ownership and Platform Rights">
            <p>
              Client owns their domain, Client-provided logo, brand assets, written content, images, and other materials provided by Client.
            </p>
            <p>
              Upon full payment, Client may use and manage the completed client-specific website created under this Offer within Client’s own Lovable account, subject to any applicable rights, licenses, limitations, or terms imposed by Lovable or other third-party platforms.
            </p>
            <p>
              Freelancer retains ownership of Freelancer’s pre-existing intellectual property and general design methods, systems, frameworks, code structures, templates, reusable components, concepts, workflows, processes, strategies, and other materials that were not created exclusively for Client.
            </p>
            <p>
              Nothing in this Agreement transfers ownership of Freelancer’s internal business systems, templates, methods, processes, or reusable materials to Client.
            </p>
          </Section>

          <Section title="11. Attribution Requirement">
            <p>
              Client agrees that the completed website may include a visible footer credit or similar acknowledgment stating that the website was designed by Three Day Launch, unless otherwise agreed in writing.
            </p>
          </Section>

          <Section title="12. No Guarantee of Results">
            <p>
              Client acknowledges that Freelancer cannot guarantee business results, sales, traffic, leads, SEO rankings, AI search visibility, conversions, revenue, website performance, or specific technical outcomes.
            </p>
            <p>
              Results depend on many factors outside Freelancer’s control, including Client’s offer, audience, pricing, market demand, follow-up, advertising, search engine updates, competition, ongoing content creation, website maintenance, platform changes, and business operations.
            </p>
          </Section>

          <Section title="13. Confidentiality">
            <p>
              Freelancer may receive confidential business information from Client while performing services.
            </p>
            <p>
              Freelancer agrees not to disclose or improperly use Client confidential information except as reasonably necessary to perform the services, operate the project, use approved team members, contractors, or tools, comply with law, or as otherwise authorized by Client.
            </p>
            <p>
              Client also agrees not to disclose Freelancer’s proprietary systems, methods, pricing structures, processes, templates, workflows, code structures, strategies, or internal business materials.
            </p>
          </Section>

          <Section title="14. Publicity and Portfolio Rights">
            <p>
              Unless Client requests confidentiality in writing before project launch, Client agrees Freelancer may use Client’s business name, logo, publicly available brand materials, and non-confidential completed project work for portfolio, website, advertising, promotional content, social media, case studies, awards, blogs, and similar purposes.
            </p>
            <p>
              Freelancer will not knowingly disclose Client’s confidential information, private customer information, unpublished proprietary materials, revenue data, or sensitive internal business information without written permission.
            </p>
          </Section>

          <Section title="15. Reuse of Design Elements">
            <p>
              Client acknowledges that Freelancer retains ownership of general design methods, systems, frameworks, code structures, templates, layouts, concepts, workflows, processes, and non-exclusive creative elements used in the project.
            </p>
            <p>
              Freelancer may reuse, adapt, or incorporate such elements into future projects, provided Client’s confidential information, proprietary content, customer information, or unique branding assets are not reused without consent.
            </p>
          </Section>

          <Section title="16. Client Content, Claims, and Legal Compliance">
            <p>
              Client is solely responsible for the accuracy, legality, ownership, and appropriateness of all content, claims, images, testimonials, offers, pricing, policies, forms, downloads, email opt-ins, and materials provided to Freelancer or published on Client’s website.
            </p>
            <p>
              Client agrees not to provide materials that infringe another party’s copyright, trademark, privacy rights, publicity rights, or other rights.
            </p>
            <p>
              Client is responsible for ensuring that their website content, offers, policies, disclaimers, accessibility requirements, privacy practices, email marketing practices, testimonials, professional claims, income claims, health claims, financial claims, and business operations comply with applicable laws and industry requirements.
            </p>
            <p>
              Freelancer does not provide legal, financial, medical, tax, accessibility, privacy, or compliance advice.
            </p>
          </Section>

          <Section title="17. Indemnification">
            <p>
              Client agrees to indemnify, defend, and hold harmless Freelancer and its owners, employees, contractors, representatives, successors, and assigns from claims, damages, liabilities, costs, and expenses, including reasonable attorneys’ fees, arising out of Client-provided content, Client’s business operations, Client’s website claims, products or services sold by Client, copyright or trademark infringement in Client materials, privacy or compliance issues related to Client’s business, Client’s use or modification of the website after delivery, Client’s violation of platform or third-party terms, or Client’s negligence or misconduct, except to the extent caused by Freelancer’s gross negligence or willful misconduct.
            </p>
          </Section>

          <Section title="18. Limitation of Liability">
            <p>
              Except as expressly stated in this Agreement, Freelancer makes no warranties, express or implied.
            </p>
            <p>
              Freelancer shall not be liable for indirect, incidental, special, consequential, punitive, lost profit, lost revenue, lost data, lost opportunity, or business interruption damages.
            </p>
            <p>
              Freelancer’s total liability for any claim shall not exceed the amount actually paid by Client to Freelancer for the specific service giving rise to the claim.
            </p>
          </Section>

          <Section title="19. Chargebacks and Payment Disputes">
            <p>
              Client agrees to contact Freelancer in writing before initiating a chargeback, payment dispute, or processor claim so the parties can attempt to resolve the concern directly.
            </p>
            <p>
              If Client initiates a chargeback for amounts validly owed under this Agreement, Freelancer may pause work, withhold final delivery, cease remaining project services, or pursue collection of outstanding amounts, including reasonable costs and fees where permitted by law.
            </p>
          </Section>

          <Section title="20. Independent Contractor">
            <p>
              Freelancer is an independent contractor and not an employee, partner, joint venturer, or agent of Client.
            </p>
            <p>
              Nothing in this Agreement authorizes Freelancer to bind Client to contracts, represent Client legally, or act as Client’s employee without written consent.
            </p>
          </Section>

          <Section title="21. Contractor and Team Support">
            <p>
              Freelancer may use employees, contractors, subcontractors, consultants, designers, developers, copywriters, assistants, specialists, or other team members to help perform services under this Agreement.
            </p>
            <p>
              Freelancer remains responsible for managing the work performed by its team and contractors. Client understands that not every task will be performed personally by Freelancer.
            </p>
            <p>
              Freelancer will take reasonable steps to ensure any team member or contractor with access to Client confidential information uses that information only as needed to perform the services.
            </p>
          </Section>

          <Section title="22. Force Majeure">
            <p>
              Freelancer shall not be liable for delay or failure in performance caused by events beyond reasonable control, including but not limited to illness, emergency, fire, flood, severe weather, accident, power outage, telecommunications failure, internet or network failure, acts of God, platform outages, terrorism, civil unrest, labor disputes, or other circumstances beyond Freelancer’s reasonable control.
            </p>
            <p>
              If Freelancer becomes unable to perform due to sudden illness, incapacity, death, emergency, or other unforeseeable circumstance, Freelancer may provide a replacement service provider subject to Client approval, reschedule the project, or refund payments made for unperformed services.
            </p>
          </Section>

          <Section title="23. Governing Law">
            <p>
              This Agreement shall be governed by the laws of the State of Illinois, without regard to conflict of law principles.
            </p>
            <p>
              Any dispute arising under this Agreement shall be handled in the state or federal courts located in Illinois, unless otherwise required by law.
            </p>
          </Section>

          <Section title="24. Severability and Waiver">
            <p>
              If any provision of this Agreement is found unenforceable, the remaining provisions remain in full force and effect.
            </p>
            <p>
              Failure to enforce any provision shall not constitute waiver of future enforcement rights.
            </p>
          </Section>

          <Section title="25. Acceptance of Terms">
            <p>
              By purchasing this Offer, checking the agreement box at checkout, submitting payment information, or beginning the project, Client confirms they have read, understood, and agreed to these Terms of Purchase in full.
            </p>
          </Section>
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-4xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm text-muted-foreground">© 2026 Three Day Launch. Custom Websites.</span>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back home</Link>
        </div>
      </footer>
    </div>
  );
}
